import { Spacing } from '@persona-id/design-tokens';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import {
  Children,
  cloneElement,
  ComponentPropsWithoutRef,
  ComponentType,
  ReactElement,
  ReactNode,
} from 'react';
import styled, { DefaultTheme, StyledComponent } from 'styled-components';

import Card, { CardProps } from 'design-system/components/Card';
import { paragraphMixins } from 'design-system/mixins/text';
import { fadeIn } from 'design-system/variables/keyframes';
import { BASE } from 'design-system/variables/spacing';
import { TransitionDuration } from 'design-system/variables/transitions';

interface FloatingTooltipProps extends CardProps {
  zIndex?: number;
  contentAlignment: 'start' | 'center' | 'end';
}

export const FloatingTooltip: StyledComponent<
  ComponentType<ComponentPropsWithoutRef<typeof Card>>,
  DefaultTheme,
  FloatingTooltipProps
> = styled(Card)<FloatingTooltipProps>`
  ${paragraphMixins.md};
  box-shadow: 0px 0px 14px 2px rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  display: flex;
  align-items: ${({ contentAlignment }) => contentAlignment || 'center'};
  justify-content: center;
  padding: ${Spacing[2]};
  animation: ${fadeIn} ${TransitionDuration.Fast} ease-in-out;
  z-index: ${({ zIndex }) => zIndex};
  margin: 0;
  max-width: 250px;
`;

const TooltipArrow = styled.div`
  width: 0;
  height: 0;
  border-color: ${({ theme }) => theme.color.background.primary};
  border-left: 8px solid transparent;
  border-top: 8px solid ${({ theme }) => theme.color.background.primary};
  border-right: 8px solid transparent;
`;

const TooltipIcon = styled.div`
  display: inline-flex;
`;

interface TooltipBaseProps
  extends Omit<TooltipPrimitive.TooltipProps, 'disableHoverableContent'>,
    TooltipPrimitive.TooltipContentProps {
  /**
   * Optionally hide tooltip arrow.
   */
  hideArrow?: boolean;

  children?: ReactNode;

  /**
   * Disabling will prevent tooltip from rendering on the trigger
   */
  disabled?: boolean;

  /**
   * Render in a portal.
   * Disable to render adjacent to trigger for rendering within elements such as modals
   */
  portal?: boolean;

  /**
   * Props applied to Radix UI Portal component
   */
  portalProps?: TooltipPrimitive.TooltipPortalProps;

  /**
   * Determines if the tooltip closes when the mouse hovers into the tooltip content area
   */
  interactive?: boolean;

  /**
   * Override the zIndex in case the tooltip is used in situations where the content is
   * already elevated
   */
  zIndex?: number;

  /**
   * Props to apply to Tooltip.Trigger within
   */
  triggerProps?: TooltipPrimitive.TooltipTriggerProps;
}

interface TooltipStandardProps extends TooltipBaseProps {
  /**
   * Element that will trigger the tooltip. A `ref` will be set.
   */
  trigger: ReactElement;

  iconTrigger?: never;

  className?: never;

  contentAlignment?: 'start' | 'center' | 'end';
}

export interface TooltipWithIconProps extends TooltipBaseProps {
  trigger?: never;

  /**
   * Icon that will trigger the tooltip. A wrapper div will be set.
   */
  iconTrigger: ReactElement;

  className?: string;

  contentAlignment?: 'start' | 'center' | 'end';
}

type TooltipProps = TooltipStandardProps | TooltipWithIconProps;

const Tooltip = ({
  trigger: triggerProp,
  iconTrigger,
  side = 'bottom',
  hideArrow,
  defaultOpen,
  open,
  delayDuration = 0,
  onOpenChange,
  interactive = true,
  children,
  'aria-label': ariaLabel,
  portal = true,
  disabled = false,
  className,
  zIndex = 10,
  triggerProps,
  portalProps,
  contentAlignment,
  ...contentProps
}: TooltipProps) => {
  let trigger;

  if (iconTrigger) {
    trigger = (
      <TooltipIcon tabIndex={0} aria-label={ariaLabel} className={className}>
        {cloneElement(iconTrigger, { focusable: false, 'aria-hidden': true })}
      </TooltipIcon>
    );
  } else {
    trigger = triggerProp;
  }

  // quick way to check if children are renderable. null, false, and true will be filtered out when converting to an array
  const hasRenderableChildren = Children.toArray(children).length > 0;

  const content = (
    <TooltipPrimitive.Content side={side} sideOffset={BASE} asChild {...contentProps}>
      <FloatingTooltip zIndex={zIndex} contentAlignment={contentAlignment}>
        {children}
        {!hideArrow && (
          <TooltipPrimitive.Arrow asChild>
            <TooltipArrow />
          </TooltipPrimitive.Arrow>
        )}
      </FloatingTooltip>
    </TooltipPrimitive.Content>
  );

  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration}>
      <TooltipPrimitive.Root
        open={open}
        defaultOpen={defaultOpen}
        disableHoverableContent={!interactive}
        onOpenChange={onOpenChange}
      >
        <TooltipPrimitive.Trigger asChild aria-label={ariaLabel} {...triggerProps}>
          {trigger}
        </TooltipPrimitive.Trigger>

        {!disabled &&
          hasRenderableChildren &&
          (portal ? (
            <TooltipPrimitive.Portal {...portalProps}>{content}</TooltipPrimitive.Portal>
          ) : (
            content
          ))}
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
};

export default Tooltip;
